import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import { router } from "./router";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && error.message.includes("4")) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

// // Suppress only the specific React warnings about pixi component casing
// // This is a known issue with @pixi/react v8 that uses lowercase component names
// const originalError = console.error;
// console.error = function (...args: unknown[]) {
//   // Only suppress the specific warning about pixi components
//   if (
//     args[0] &&
//     typeof args[0] === "string" &&
//     args[0].includes("is using incorrect casing") &&
//     args[0].includes("<pixi")
//   ) {
//     return;
//   }
//   // Call original console.error for all other errors
//   return originalError.apply(console, args);
// };

ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
);

// Register service worker EARLY (don't wait for window load so we can intercept initial image requests)
if ("serviceWorker" in navigator) {
  const isHttpProtocol =
    window.location.protocol === "http:" || window.location.protocol === "https:";

  if (!isHttpProtocol) {
    console.log(
      "Service Worker not registered: requires HTTP/HTTPS protocol"
    );
  } else {
    navigator.serviceWorker
      .register("./sw.js")
      .then((registration) => {
        console.log(
          "Service Worker registered successfully (early):",
          registration.scope
        );
        // Force update attempt in case a previous cached version exists
        registration.update().catch(() => {});

        const sendWhenActive = (sw: ServiceWorker | null) => {
          if (sw && sw.state === "activated") {
            sendConfigToSW(sw);
          } else if (sw) {
            sw.addEventListener("statechange", () => {
              if (sw.state === "activated") {
                sendConfigToSW(sw);
              }
            });
          }
        };
        sendWhenActive(registration.active || registration.installing || registration.waiting);
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });

    // Optional self-destruct ONLY when explicitly requested (e.g. add ?swSelfDestruct to URL)
    if (window.location.search.includes("swSelfDestruct")) {
      window.addEventListener("beforeunload", () => {
        if (navigator.serviceWorker.controller) {
          try {
            navigator.serviceWorker.controller.postMessage({
              type: "SELF_DESTRUCT",
            });
          } catch (e) {
            // ignore
          }
        }
        navigator.serviceWorker.getRegistration("./sw.js").then((reg) => {
          if (reg) {
            reg.unregister().then((success) => {
              if (success) {
                console.log("Service Worker unregistered on unload (swSelfDestruct mode)");
              }
            });
          }
        });
      });
    }
  }
}

// Helper to send config to SW
function sendConfigToSW(worker: ServiceWorker) {
  const config = {
    type: 'CONFIG_UPDATE',
    config: {
      maxImageSize: 2 * 1024 * 1024, // 2MB limit
      logPrefix: 'SonicUISW:'
      // No staticAssets - SW is image-only
    }
  };

  worker.postMessage(config);
  console.log('Sent config to SW:', config);
}
